import React from 'react';

import IndexedSubSectionWrapper from "hoc/IndexedSubSectionWrapper";
import * as ResourceApi from "../../../../../api/resources";
import {createButtonSectionFactory} from "hoc/CreateButtonSection";
import {FBCommentQuestionToolTip} from 'components/break/tooltips/breaks-steps-section-tool-tips';
import {isNoU} from 'utils/DBUtils';

import {FormattedMessage, IntlProvider} from 'react-intl';
import {enUSmessage} from 'i18n/en-US.js';

export default {
    name: "filtering",
    subSection: true,
    resource: true,
    hideIfResourceIsNoU: true,

    fields: [
        {
            type: 'contenteditable',
            name: 'headline',
            required: true,
            // Headline is only allowed if there is more than one step
            showField: (proxy) => (proxy.currentBaseResource.steps && proxy.currentBaseResource.steps.length > 1),

            props: {
                labelKey: 'editbreak.steps.filtering.headline'
            }
        },
        {
            type: 'contenteditable',
            name: 'introCopy',
            required: false,
            props: {
              labelKey: 'editbreak.steps.filtering.intro_copy',
              maxLength: 500,
              placeholderKey: 'editbreak.steps.filtering.intro_copy.placeholder',
              validationRules: [
                  {
                      type: 'MarkupTextLimit',
                      params: {
                          limit: 500
                      }
                  }
              ]
            }
        },
        {
            type: 'image',
            name: 'headerImageId',
            idFor: 'headerImage',
            required: true,
            props: {
                labelKey: 'editbreak.steps.filtering.header_image',
                imageType: 'stepHeaderImage'
            }
        },
        {
            type: 'color',
            name: 'resultBkgdColor',
            required: true,
            showField: (proxy) => (proxy.currentBaseResource.sponsoredType === 'sponsored'),
            linkedField: (formProxy) => ["missionColor7"],
            props: {
                labelKey: 'editbreak.steps.filtering.result_bg_color'
            }
        },
        {
          type: 'radio',
          name: 'resultLayoutType',
          required: true,
          props: {
            labelKey: 'editbreak.steps.filtering.result_layout',
            options: [
              {
                value: 'SINGLE_COLUMN',
                textKey: 'editbreak.steps.filtering.result_layout.single_column'
              },
              {
                value: 'TWO_COLUMN',
                textKey: 'editbreak.steps.filtering.result_layout.two_column'
              }
            ]
          }
        },
        {
          name: 'dropdowns',
          subSection: true,
          resource: true,
          collection: true,
          sectionWrapper: IndexedSubSectionWrapper,
          deletable: true,
          canDelete: (formProxy) => {
            var parent = formProxy.parentCurrentResource;
            return parent.dropdowns && parent.dropdowns.length > 1;
          },
          canCreate: (formProxy) => (formProxy.parentCurrentResource.dropdowns.length < 3),
          sortable: true,
          sortableName: "filteringDropdownsOrder",
          toggleSortLabelKey: 'editbreak.steps.filtering.dropdown.reorder_toggle',
          resourceType: ResourceApi.Types.FilteringDropdown,

          props: {
              headerKey: 'editbreak.steps.filtering.dropdown.header',
              getCustomSectionHeader: (dropdown, sectionIndex) => {
                return  dropdown.label
                        && dropdown.label.trim() !== "" ? "Dropdown " + (sectionIndex+1) + ": " + dropdown.label : null;
              }
          },

          footerContent: createButtonSectionFactory(ResourceApi.Types.FilteringDropdown),

          fields: [
            {
              type: 'yesnoradio',
              name: 'showLabel',
              required: true,
              props: {
                labelKey: 'editbreak.steps.filtering.dropdown.show_label'
              }
            },
            {
              type: 'text',
              name: 'label',
              required: true,
              props: {
                labelKey: 'editbreak.steps.filtering.dropdown.label'
              }
            },
            {
              name: 'items',
              subSection: true,
              resource: true,
              collection: true,
              sectionWrapper: IndexedSubSectionWrapper,
              deletable: true,
              canDelete: (formProxy) => {
                var dropdown = formProxy.parentCurrentResource;
                return dropdown.items && dropdown.items.length > 1;
              },

              sortable: true,
              sortableName: "filteringDropdownItemsOrder",
              toggleSortLabelKey: 'editbreak.steps.filtering.dropdown.items.reorder_toggle',
              resourceType: ResourceApi.Types.FilteringDropdownItem,

              props: {
                  headerKey: 'editbreak.steps.filtering.dropdown.items.header',
                  getCustomSectionHeader: (item, sectionIndex) => {
                    return  item.text
                            && item.text.trim() !== "" ? "Dropdown Item " + (sectionIndex+1) + ": " + item.text : null;
                  }
              },

              footerContent: createButtonSectionFactory(ResourceApi.Types.FilteringDropdownItem),

              fields: [
                {
                  type: 'text',
                  name: 'text',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.filtering.dropdown.items.text'
                  }
                },
                {
                  type: 'text',
                  name: 'value',
                  required: true,
                  linkedField: (proxy) => {
                    var path = proxy.resourcePath;
                    path.push("text");
                    return path;
                  },
                  props: {
                    labelKey: 'editbreak.steps.filtering.dropdown.items.value'
                  }
                },
                {
                  type: 'dynamicContentRules',
                  name: 'dynamics',
                  required: true,
                  showField: proxy => Boolean(proxy.currentBaseResource.steps.find(step => step.type === 'PERSONALITY_QUIZ')),
                  props: {
                    labelKey: 'editbreak.steps.listicle.item.dynamics'
                  }
                }
              ]
            } // end dropdown items
          ]
        }, // end dropdown section
        {
          name: 'results',
          subSection: true,
          resource: true,
          collection: true,
          sectionWrapper: IndexedSubSectionWrapper,
          deletable: true,
          canDelete: (formProxy) => {
            var parent = formProxy.parentCurrentResource;
            return parent.results && parent.results.length > 1;
          },
          sortable: true,
          sortableName: "filteringResultsOrder",
          toggleSortLabelKey: 'editbreak.steps.filtering.results.reorder_toggle',
          resourceType: ResourceApi.Types.FilteringResult,

          props: {
              headerKey: 'editbreak.steps.filtering.results.header',
              getCustomSectionHeader: (result, sectionIndex) => {
                return  result.title
                        && result.title.trim() !== "" ? "Result " + (sectionIndex+1) + ": " + result.title : null;
              }
          },

          footerContent: createButtonSectionFactory(ResourceApi.Types.FilteringResult),

          fields: [
            {
              type: 'text',
              name: 'title',
              required: true,
              props: {
                labelKey: 'editbreak.steps.filtering.results.title'
              }
            },
            {
              type: 'image',
              name: 'imageId',
              idFor: 'image',
              required: true,
              props: {
                labelKey: 'editbreak.steps.filtering.results.image',
                imageType: 'filteringResult'
              }
            },
            {
              type: 'filterAssociateDropdownWithResultsTable',
              name: 'associatedItems',
              props: {
                dropdowns: (proxy) => (proxy.parentCurrentResource.dropdowns),
                associatedItems: (proxy) => (proxy.currentResource.associatedItems)
              }
            },
            {
              type: 'yesnoradio',
              name: 'sharingOn',
              required: true,
              props: {
                labelKey: 'editbreak.steps.filtering.results.sharing_on'
              }
            },
            {
              name: 'contents',
              subSection: true,
              resource: true,
              collection: true,
              sectionWrapper: IndexedSubSectionWrapper,
              deletable: true,

              props: {
                  getCustomSectionHeader: (content) => {
                    // TODO:
                    return content.type;
                  },
                  headerKey: 'editbreak.steps.filtering.result.contents.header'
              },

                sortable: true,
                sortableName: "filteringResultContentOrder",
                toggleSortLabelKey: 'editbreak.steps.filtering.result.content.reorder_toggle',
                resourceType: ResourceApi.Types.FilteringResultContent,

              footerContent: createButtonSectionFactory(
                ResourceApi.Types.FilteringResultBodyCopy,
                ResourceApi.Types.FilteringResultImage,
                ResourceApi.Types.FilteringResultLinkButton,
                ResourceApi.Types.FilteringResultBlockQuote,
                ResourceApi.Types.FilteringResultEmbed,
              ),

              fields: [
                {
                  name: 'imageType',
                  type: 'dropdown',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Image'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.image.type',
                    options: [
                      {
                        value: 'CUSTOM',
                        textKey: 'editbreak.steps.filtering.result.content.image.type.custom'
                      },
                      {
                        value: 'HORIZONTAL',
                        textKey: 'editbreak.steps.filtering.result.content.image.type.horizontal'
                      },
                      {
                        value: 'VERTICAL',
                        textKey: 'editbreak.steps.filtering.result.content.image.type.vertical'
                      }
                    ]
                  }
                },
                {
                  name: 'scaling',
                  type: 'radio',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Image' && !isNoU(proxy.currentResource.imageType)),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.image.scaling',
                    options: [
                      {
                        value: 100,
                        textKey: 'editbreak.steps.filtering.result.content.image.scaling.none'
                      },
                      {
                        value: 65,
                        textKey: 'editbreak.steps.filtering.result.content.image.scaling.65'
                      },
                      {
                        value: 75,
                        textKey: 'editbreak.steps.filtering.result.content.image.scaling.75'
                      }
                    ]
                  }
                },
                // TODO: Update imageType prop to be a method and not need multiple instances
                {
                  name: 'imageId',
                  type: 'image',
                  idFor: 'image',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Image' && proxy.currentResource.imageType === 'CUSTOM'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.image.image',
                    imageType: 'custom'
                  }
                },
                {
                  name: 'imageId',
                  type: 'image',
                  idFor: 'image',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Image' && proxy.currentResource.imageType === 'HORIZONTAL'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.image.image',
                    imageType: 'listicleHorizontal'
                  }
                },
                {
                  name: 'imageId',
                  type: 'image',
                  idFor: 'image',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Image' && proxy.currentResource.imageType === 'VERTICAL'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.image.image',
                    imageType: 'listicleVertical'
                  }
                },
                {
                  type: 'contenteditable',
                  name: 'bodyCopy',
                  required: true,
                  showField: (proxy) => {return proxy.currentResource.type === 'BodyCopy'},
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.body_copy.text'
                  }
                },
                {
                  type: 'textarea',
                  name: 'embedCode',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'EmbedCode'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.embed.code'
                  }
                },
                {
                  name: 'blockQuote',
                  type: 'contenteditable',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'BlockQuote'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.block_quote.value',
                    placeholderKey: 'editbreak.steps.filtering.result.content.block_quote.value.placeholder'
                  }
                },
                {
                  name: 'blockQuoteSource',
                  type: 'contenteditable',
                  required: false,
                  showField: (proxy) => (proxy.currentResource.type === 'BlockQuote'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.block_quote_source.value',
                    placeholderKey: 'editbreak.steps.filtering.result.content.block_quote_source.value.placeholder'
                  }
                },
                {
                  name: 'buttonText',
                  type: 'text',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Button'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.button.text'
                  }
                },
                {
                  name: 'url',
                  type: 'text',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Button'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.button.url'
                  }
                },
                {
                  name: 'textColor',
                  type: 'color',
                  required: false,
                  showField: (proxy) => (proxy.currentResource.type === 'Button'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.button.text_color'
                  }
                },
                {
                  name: 'bgColor',
                  type: 'color',
                  required: false,
                  showField: (proxy) => (proxy.currentResource.type === 'Button'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.button.bg_color'
                  }
                },
                {
                  type: 'radio',
                  name: 'placement',
                  required: true,
                  showField: (proxy) => (proxy.grandParentCurrentResource.resultLayoutType !== 'SINGLE_COLUMN'),
                  props: {
                    labelKey: 'editbreak.steps.filtering.result.content.placement',
                    options: [
                      {
                        textKey: 'editbreak.steps.filtering.result.content.placement.left',
                        value: 'LEFT'
                      },
                      {
                        textKey: 'editbreak.steps.filtering.result.content.placement.right',
                        value: 'RIGHT'
                      }
                    ]
                  }
                }
              ]
            }
          ]
        } // end results
    ]
}



// WEBPACK FOOTER //
// ./public/components/EditContent/configs/breaks/steps/filtering.js